import { memo } from "react";

import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, IconButton, List, ListItem, ListItemButton, ListItemText, useTheme } from "@mui/material";
import LocationType from "src/types/LocationType";

import { getStyles, isParentMenuItemSelected } from "../sidebar.helpers";
import { LocationRow } from "./LocationRow";

interface RegionGroupProps {
  open: boolean;
  locationType: LocationType;
  region: { id: string; label: string };
  children: Array<{ id: string; label: string }>;
  pathname: string;
  isSingleRegion: boolean;
  isVirtualRegion: boolean;
  regionExpanded: boolean;
  onToggleRegion: () => void;
  onNavigateRegion: (regionId: string) => void;
  onSelectLocation: (id: string) => void;
}

export const RegionGroup = memo(function RegionGroup({
  open,
  locationType,
  region,
  children,
  pathname,
  isSingleRegion,
  isVirtualRegion,
  regionExpanded,
  onToggleRegion,
  onNavigateRegion,
  onSelectLocation,
}: RegionGroupProps) {
  const theme = useTheme();

  return (
    <Box key={region.label} sx={getStyles(theme).border}>
      {!isSingleRegion && !isVirtualRegion && (
        <ListItem
          secondaryAction={
            <IconButton edge='end' onClick={onToggleRegion} aria-label={regionExpanded ? "Collapse region" : "Expand region"}>
              {regionExpanded ? <ExpandLess sx={getStyles(theme).icon} /> : <ExpandMore sx={getStyles(theme).icon} />}
            </IconButton>
          }
        >
          <ListItemButton
            selected={isParentMenuItemSelected(pathname, region.id, locationType)}
            sx={getStyles(theme).listItemButton(open)}
            onClick={() => onNavigateRegion(region.id)}
            aria-expanded={regionExpanded}
          >
            <ListItemText
              primaryTypographyProps={getStyles(theme).listItemPrimaryTypographyProps}
              primary={region.label}
              sx={getStyles(theme).listItemText(open)}
            />
          </ListItemButton>
        </ListItem>
      )}

      <Collapse in={isSingleRegion || isVirtualRegion || regionExpanded} timeout='auto' unmountOnExit>
        <List>
          {children.map((loc) => (
            <LocationRow
              key={loc.id}
              open={open}
              locationType={locationType}
              location={loc}
              pathname={pathname}
              onSelect={onSelectLocation}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
});
